// Ventas › Facturas / Boletas (pedido de Rodrigo, 2026-10-26: «las que mandan son los DTE»). Cada
// fila es un documento tributario: tipo, folio, fecha, cliente, monto y estado SII, con «Ver» e
// «Imprimir» a la mano. La nota de venta es su origen (una columna chica); la venta del punto de venta
// dice «Punto de venta». Enter o doble clic abre la ficha del documento.
import { useState } from 'react';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { fecha, pesos, rut } from '../comun/formato';
import { hoy } from '../compras/comun';
import {
  textoEstado,
  tonoEstado,
  useDocumentosEmitidos,
  type DocumentoEmitido,
  type FiltroDocumentos,
} from '../dte/documentosEmitidos';
import { imprimirDte, NOMBRE_DTE, nombreDte, tituloDte, verPdfDte } from '../dte/pdfDte';
import { useSesion } from '../sesion/SesionContexto';

const CANAL: Record<string, string> = {
  MESON: 'Punto de venta',
  CALL_CENTER: 'Call center',
  VENDEDOR: 'Vendedor',
  B2B: 'Portal B2B',
};

export function DocumentosEmitidos({
  alAbrirDte,
  alAbrirNota,
  tipoInicial,
}: {
  alAbrirDte: (documentoId: string, titulo: string) => void;
  alAbrirNota: (notaId: string, numero: string) => void;
  tipoInicial?: string | undefined;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [f, setF] = useState<FiltroDocumentos>({
    desde: `${hoy().slice(0, 8)}01`,
    hasta: hoy(),
    pagina: 1,
    ...(tipoInicial ? { tipoDte: tipoInicial } : {}),
  });
  // El cliente se busca al apretar Enter o al salir del campo (no en cada letra).
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<unknown>(null);
  const q = useDocumentosEmitidos(empresaId, f);
  const cambiar = (c: Partial<FiltroDocumentos>) => setF((x) => ({ ...x, ...c, pagina: 1 }));
  const buscarCliente = () => cambiar({ cliente: texto.trim() || undefined });
  const correr = (p: Promise<void>) => {
    setError(null);
    p.catch((e: unknown) => setError(e));
  };
  const columnas: Columna<DocumentoEmitido>[] = [
    { id: 'tipo', titulo: 'Tipo', celda: (d) => nombreDte(d.tipoDte), ancho: '8rem' },
    {
      id: 'folio',
      titulo: 'Folio',
      celda: (d) => <b className="mono">{d.folio ?? '—'}</b>,
      alinear: 'der',
      ancho: '6rem',
    },
    { id: 'fecha', titulo: 'Emisión', celda: (d) => fecha(d.fechaEmision), ancho: '7rem' },
    { id: 'cliente', titulo: 'Cliente', celda: (d) => d.receptorRazonSocial ?? '—' },
    {
      id: 'rut',
      titulo: 'RUT',
      celda: (d) => <span className="mono">{d.receptorRut ? rut(d.receptorRut) : '—'}</span>,
      ancho: '9rem',
    },
    { id: 'total', titulo: 'Total', celda: (d) => pesos(d.total), alinear: 'der', ancho: '8rem' },
    {
      id: 'estado',
      titulo: 'Estado',
      celda: (d) => <span className={`insignia ${tonoEstado(d)}`}>{textoEstado(d)}</span>,
      ancho: '10rem',
    },
    {
      id: 'origen',
      titulo: 'Origen',
      celda: (d) => {
        const o = d.origen;
        if (!o) return <span className="apagado">—</span>;
        if (o.tipo === 'VENTA_DIRECTA')
          return <span className="apagado">{CANAL[o.canal] ?? 'Venta directa'}</span>;
        if (o.tipo === 'AJUSTE')
          return (
            <span className="apagado">
              {o.referido ? `Corrige ${tituloDte(o.referido.tipoDte, o.referido.folio)}` : '—'}
            </span>
          );
        return (
          <button
            type="button"
            className="enlace mono apagado"
            title="La nota de venta que reservó el stock"
            onClick={() => alAbrirNota(o.notaId, o.numeroVisible)}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {o.numeroVisible}
          </button>
        );
      },
      ancho: '11rem',
    },
    {
      id: 'acciones',
      titulo: '',
      celda: (d) =>
        d.estado === 'EMITIDO' &&
        d.folio &&
        puede('dte.ver') && (
          <span className="acciones-fila" onDoubleClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="boton-mini"
              title="Abre el PDF en otra pestaña"
              onClick={() => correr(verPdfDte(d.documentoId))}
            >
              Ver
            </button>
            <button
              type="button"
              className="boton-mini"
              title="Directo a la impresora"
              onClick={() => correr(imprimirDte(d.documentoId))}
            >
              Imprimir
            </button>
          </span>
        ),
      ancho: '10rem',
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="documentos-emitidos">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input
            type="date"
            aria-label="Desde"
            value={f.desde ?? ''}
            onChange={(e) => cambiar({ desde: e.target.value || undefined })}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input
            type="date"
            aria-label="Hasta"
            value={f.hasta ?? ''}
            onChange={(e) => cambiar({ hasta: e.target.value || undefined })}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Tipo</span>
          <select
            aria-label="Tipo de documento"
            value={f.tipoDte ?? ''}
            onChange={(e) => cambiar({ tipoDte: e.target.value || undefined })}
          >
            <option value="">Todos</option>
            {[33, 39, 61, 56, 34, 41, 52].map((t) => (
              <option key={t} value={t}>
                {NOMBRE_DTE[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="campo campo-corto">
          <span className="etiqueta">Folio</span>
          <input
            inputMode="numeric"
            aria-label="Folio"
            placeholder="todo el historial"
            value={f.folio ?? ''}
            onChange={(e) => cambiar({ folio: e.target.value.replace(/\D/g, '') || undefined })}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Cliente o RUT</span>
          <input
            aria-label="Cliente o RUT"
            placeholder="nombre o RUT + Enter"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              if (!e.target.value.trim()) cambiar({ cliente: undefined });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') buscarCliente();
            }}
            onBlur={buscarCliente}
          />
        </label>
      </div>
      {(q.isError || !!error) && <PanelError error={q.error ?? error} />}
      <Grilla
        etiqueta="Facturas y boletas"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(d) => d.documentoId}
        cargando={q.isFetching}
        autofoco
        vacio={
          f.folio
            ? 'No hay documentos con ese folio'
            : 'Sin documentos en el período (cambia las fechas o busca por folio)'
        }
        alAbrir={(d) => alAbrirDte(d.documentoId, tituloDte(d.tipoDte, d.folio))}
        paginacion={{
          pagina: f.pagina ?? 1,
          hayMas: !!q.data?.hayMas,
          alCambiar: (pagina) => setF((x) => ({ ...x, pagina })),
        }}
      />
      <p className="ayuda">
        Enter o doble clic abre el documento (detalle, PDF, cedible, XML y sus notas de crédito). El
        folio busca en todo el historial.
      </p>
    </div>
  );
}
